/**
 * Central Hub for Iframe Communication
 * Singleton service to manage all communication with the Escalated View iframe
 */

class IframeCommHub {
  constructor() {
    if (IframeCommHub.instance) {
      return IframeCommHub.instance;
    }

    this.iframe = null;
    this.isReady = false;
    this.messageQueue = [];
    this.listeners = new Map();
    this.messageHandlers = new Map();
    
    // Bind methods
    this.handleIframeMessage = this.handleIframeMessage.bind(this);
    
    IframeCommHub.instance = this;
  }

  initialize(iframeElement) {
    if (this.iframe) {
      return;
    }
    this.iframe = iframeElement;
    window.addEventListener("message", this.handleIframeMessage);
    
  }

  destroy() {
    window.removeEventListener("message", this.handleIframeMessage);
    this.iframe = null;
    this.isReady = false;
    this.messageQueue = [];
    this.listeners.clear();
    this.messageHandlers.clear();
  }

  setReady(ready) {
    this.isReady = ready;
    
    if (ready && this.messageQueue.length > 0) {
      this.messageQueue.forEach(({ type, payload, callback }) => {
        this.sendMessage(type, payload, callback);
      });
      this.messageQueue = [];
    }

    this.notifyListeners("ready", ready);
  }

  sendMessage(type, payload, callback = null) {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.warn(`Iframe not available, queueing message: ${type}`);
      this.messageQueue.push({ type, payload, callback });
      return;
    }

    if (!this.isReady) {
      console.warn(`Iframe not ready, queueing message: ${type}`);
      this.messageQueue.push({ type, payload, callback });
      return;
    }

    const message = {
      type,
      payload,
      timestamp: new Date().toISOString(),
      source: "guardian-cms-ui",
      messageId: this.generateMessageId(),
    };

    // Store callback if provided
    if (callback) {
      this.messageHandlers.set(message.messageId, callback);
    }

    this.iframe.contentWindow.postMessage(message, this.iframeOrigin);
    console.log("Message sent to iframe:", type, payload);

    // Notify listeners
    this.notifyListeners("messageSent", { type, payload });
  }

  /**
   * Handle messages received from iframe
   * @param {MessageEvent} event - The message event
   */
  handleIframeMessage(event) {
    // Security: Verify origin
    if (event.origin !== this.iframeOrigin) {
      console.warn("Message from untrusted origin:", event.origin);
      return;
    }

    const { type, payload, messageId, inResponseTo } = event.data;
    console.log("Message received from iframe:", type, payload);

    // If this is a response to a previous message, call the callback
    if (inResponseTo && this.messageHandlers.has(inResponseTo)) {
      const callback = this.messageHandlers.get(inResponseTo);
      callback(payload);
      this.messageHandlers.delete(inResponseTo);
    }

    // Handle specific message types
    switch (type) {
      case "IFRAME_READY":
        this.setReady(true);
        this.sendInitialConfig();
        break;
      case "REQUEST_DATA":
        this.handleDataRequest(payload);
        break;
      case "IFRAME_ERROR":
        console.error("Error from iframe:", payload);
        this.notifyListeners("error", payload);
        break;
      default:
        // Notify custom listeners
        this.notifyListeners(type, payload);
    }

    // Notify all message listeners
    this.notifyListeners("messageReceived", { type, payload });
  }

  /**
   * Handle data requests from iframe
   * @param {object} payload - Request payload
   */
  handleDataRequest(payload) {
    const { requestType } = payload;

    switch (requestType) {
      case "USER_INFO":
        this.sendUserInfo();
        break;
      case "ADDITIONAL_CONFIG":
        this.sendAdditionalConfig();
        break;
      case "SESSION_DATA":
        this.sendSessionData();
        break;
      default:
        console.warn("Unhandled data request:", requestType);
        // Notify listeners so they can handle custom requests
        this.notifyListeners("dataRequest", payload);
    }
  }

  /**
   * Send initial configuration to iframe
   * Sends all sessionStorage content to the iframe
   */
  sendInitialConfig() {
    // Collect all sessionStorage items
    const sessionData = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        try {
          // Try to parse as JSON, fallback to string if not valid JSON
          const value = sessionStorage.getItem(key);
          try {
            sessionData[key] = JSON.parse(value);
          } catch {
            sessionData[key] = value;
          }
        } catch (error) {
          console.warn(`Failed to read sessionStorage key "${key}":`, error);
        }
      }
    }

    const config = {
      sessionStorage: sessionData,
      environment: process.env.NODE_ENV,
    };

    this.sendMessage("INITIAL_CONFIG", config);
  }

  /**
   * Register a listener for specific events
   * @param {string} eventType - The event type to listen for
   * @param {function} callback - The callback function
   * @returns {function} Unsubscribe function
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    
    this.listeners.get(eventType).push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Remove a listener
   * @param {string} eventType - The event type
   * @param {function} callback - The callback to remove
   */
  off(eventType, callback) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  notifyListeners(eventType, data) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in listener for ${eventType}:`, error);
        }
      });
    }
  }

  generateMessageId() {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getIsReady() {
    return this.isReady;
  }

  setIframeOrigin(origin) {
    console.log("Setting iframe origin:", origin);
    this.iframeOrigin = origin;
  }

  getIframeOrigin() {
    return this.iframeOrigin;
  }
}

const iframeCommHub = new IframeCommHub();
export default iframeCommHub;

